import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

function DeleteEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      const eventData = await apiService.getEvent(id);
      setEvent(eventData);
    } catch (err) {
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: `Delete "${event?.name}"?`,
      text: "This action cannot be undone and will permanently delete all event data including registrations and proposals.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#fff',
      customClass: {
        popup: 'rounded-3xl shadow-xl',
        confirmButton: 'px-5 py-2 text-white rounded-md font-medium',
        cancelButton: 'px-5 py-2 text-gray-800 rounded-md font-medium'
      }
    });
  
    if (!result.isConfirmed) return;
  
    try {
      setDeleting(true);
      setError('');
  
      await apiService.deleteEvent(id);
  
      await Swal.fire({
        title: 'Deleted!',
        text: 'Your event has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#6c5ce7',
        timer: 2000,
        showConfirmButton: false,
      });
  
      navigate('/organizer/dashboard', { 
        state: { message: 'Event deleted successfully!' } 
      });
      
    } catch (err) {
      console.error('Delete event error:', err);
      setError(err.message || 'Failed to delete event');
      Swal.fire('Error', 'Failed to delete event. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="delete-event">
        <div className="page-header">
          <h1>Delete Event</h1>
          <button 
            onClick={() => navigate('/organizer/dashboard')}
            className="btn btn-secondary"
          >
            Back to Dashboard
          </button>
        </div>
        
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="card">
          {event && (
            <div className="event-summary">
              <h3>{event.name}</h3>
              <p><strong>Date:</strong> {new Date(event.schedule).toLocaleString()}</p>
              <p><strong>Venue:</strong> {event.venue}</p>
              <p><strong>Description:</strong> {event.description}</p>
            </div>
          )}

          <div className="alert alert-warning">
            <h3>⚠️ Critical Action: Delete Event</h3>
            <p>This action will permanently delete the event and all associated data:</p>
            <ul>
              <li>All participant registrations ({event?.registrations?.length || 0})</li>
              <li>All talk proposals ({event?.proposals?.length || 0})</li>
              <li>Event banner image</li>
              <li>All custom form submissions</li>
              <li>Event analytics and reports</li>
            </ul>
            <p><strong>This action cannot be undone!</strong></p>
          </div>

          <div className="action-buttons">
            <button 
              onClick={handleDelete}
              disabled={deleting}
              className="btn btn-danger"
            >
              {deleting ? (
                <>
                  <span className="spinner"></span>
                  Deleting...
                </>
              ) : (
                'Permanently Delete Event'
              )}
            </button>
            
            <button 
              onClick={() => navigate('/organizer/dashboard')}
              className="btn btn-outline"
            >
              Cancel Deletion
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleteEvent;
